import {
  Button,
  Chip,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import RefreshIcon from "@mui/icons-material/RefreshOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopyOutlined";
import { useCallback, useEffect, useState } from "react";
import type { PairingCode } from "@shared/types/foyer";
import { foyerApi } from "../http/foyer";

interface Props {
  premiseId: string;
  onCreateClick: () => void;
  /** Bumped by the parent after a successful create to trigger a refresh. */
  refreshTick?: number;
}

type CodeStatus = "active" | "expired";

function statusOf(p: PairingCode): CodeStatus {
  if (Date.parse(p.expiresAt) < Date.now()) return "expired";
  return "active";
}

function timeLeft(iso: string): string {
  const ms = Date.parse(iso) - Date.now();
  if (ms <= 0) return "expired";
  const mins = Math.floor(ms / 60_000);
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 48) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

export default function InstallationCodesView({
  premiseId,
  onCreateClick,
  refreshTick,
}: Props) {
  const [codes, setCodes] = useState<PairingCode[]>([]);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setCodes(await foyerApi.listPairings(premiseId));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [premiseId]);

  useEffect(() => {
    refresh();
    const t = window.setInterval(refresh, 5_000);
    return () => window.clearInterval(t);
  }, [refresh, refreshTick]);

  async function revoke(code: string) {
    if (!confirm(`Revoke installation code ${code}?`)) return;
    await foyerApi.revokePairing(code);
    refresh();
  }

  function copyCode(code: string) {
    navigator.clipboard.writeText(code).catch(() => {});
  }

  return (
    <Stack spacing={3}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="h5">Installation codes</Typography>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Refresh">
            <IconButton
              onClick={refresh}
              aria-label="Refresh codes"
              data-testid="codes-refresh"
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onCreateClick}
            data-testid="codes-new"
          >
            New code
          </Button>
        </Stack>
      </Stack>
      {error && (
        <Typography color="error" variant="body2">
          {error}
        </Typography>
      )}
      {codes.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No codes yet. Click <strong>New code</strong> to generate one.
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small" data-testid="codes-table">
            <TableHead>
              <TableRow>
                <TableCell>Code</TableCell>
                <TableCell>Created by</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Expires</TableCell>
                <TableCell>Used</TableCell>
                <TableCell>Status</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {codes.map((p) => {
                const status = statusOf(p);
                return (
                  <TableRow key={p.code} hover>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography
                          sx={{
                            fontFamily:
                              'ui-monospace, "SF Mono", Menlo, Consolas, monospace',
                            fontWeight: 600,
                            letterSpacing: "0.08em",
                          }}
                        >
                          {p.code}
                        </Typography>
                        <Tooltip title="Copy">
                          <IconButton
                            size="small"
                            aria-label="Copy code"
                            onClick={() => copyCode(p.code)}
                          >
                            <ContentCopyIcon fontSize="inherit" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {p.createdBy ?? "-"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(p.createdAt).toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2">
                          {new Date(p.expiresAt).toLocaleString()}
                        </Typography>
                        {status === "active" && (
                          <Typography variant="caption" color="text.secondary">
                            · {timeLeft(p.expiresAt)} left
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {p.redemptions?.length ?? 0}{" "}
                        {p.redemptions?.length === 1 ? "TV" : "TVs"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={status === "active" ? "success" : "default"}
                        variant={status === "active" ? "filled" : "outlined"}
                        label={status}
                      />
                    </TableCell>
                    <TableCell align="right">
                      {status === "active" && (
                        <Tooltip title="Revoke">
                          <IconButton
                            size="small"
                            aria-label={`Revoke code ${p.code}`}
                            onClick={() => revoke(p.code)}
                            data-testid={`code-revoke-${p.code}`}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Stack>
  );
}
